import React, {
  ChangeEventHandler,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useDebounce } from 'react-use';

import { ChevronLeft20, ChevronRight20 } from '@carbon/icons-react';
import { Flex, Input, Button } from '@chakra-ui/react';

interface PaginationProps {
  current: number;
  pageCount: number;
  onChange: (value: number) => void;
}

const ListItem: React.FC = ({ children }) => (
  <Flex listStyle="none" align="center">
    {children}
  </Flex>
);

const Pagination: React.FC<PaginationProps> = ({
  current,
  pageCount,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState<number>(current);
  const [debouncedInputValue, setDebouncedInputValue] = useState<number>(
    inputValue,
  );

  const [, cancelDebounce] = useDebounce(
    () => {
      setDebouncedInputValue(inputValue);
    },
    1000,
    [inputValue],
  );

  useEffect(() => {
    setInputValue(current);
    cancelDebounce();
    setDebouncedInputValue(current);
  }, [cancelDebounce, current]);

  useEffect(() => {
    if (debouncedInputValue) {
      onChange(debouncedInputValue);
    }
  }, [debouncedInputValue, onChange]);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newValue = parseInt(event.target.value, 10);

    if (newValue <= 0 || newValue > pageCount) {
      return;
    }

    setInputValue(newValue);
  };

  const changePage = useCallback(
    (diff: number) => {
      const newInputValue = inputValue + diff;
      setInputValue(newInputValue);
      cancelDebounce();
      setDebouncedInputValue(newInputValue);
    },
    [cancelDebounce, inputValue],
  );

  const onBlur = () => {
    if (Number.isNaN(inputValue)) {
      setInputValue(current);
    }
  };

  const isInputValueNaN = Number.isNaN(inputValue);

  return (
    <Flex w="full" justify="center">
      <Flex
        as="ul"
        bg="white"
        px={8}
        py={4}
        borderRadius={5}
        gridGap={2}
        onBlur={onBlur}
      >
        <ListItem>
          <Button
            disabled={isInputValueNaN || inputValue <= 1}
            colorScheme="gray"
            onClick={() => changePage(-1)}
            data-testid="previous-btn"
          >
            <ChevronLeft20 />
          </Button>
        </ListItem>
        <ListItem>
          page
          <Input
            textAlign="center"
            w={12}
            mx={2}
            p={1}
            value={inputValue}
            onChange={handleInputChange}
            type="number"
            data-testid="page-input"
          />
          of {pageCount}
        </ListItem>
        <ListItem>
          <Button
            disabled={isInputValueNaN || pageCount <= inputValue}
            colorScheme="gray"
            onClick={() => changePage(1)}
            data-testid="next-btn"
          >
            <ChevronRight20 />
          </Button>
        </ListItem>
      </Flex>
    </Flex>
  );
};

export default Pagination;
